// NOTE: Only lines with "// CHANGE:" were added/edited.

import EditorTheme from "./themes/theme";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { ListItemNode, ListNode } from "@lexical/list";
import { CodeHighlightNode, CodeNode } from "@lexical/code";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { TRANSFORMERS } from "@lexical/markdown";
import { $generateNodesFromDOM } from "@lexical/html";
import { $insertNodes, $createTextNode, $getRoot, LexicalEditor, $getSelection, $isRangeSelection, DecoratorNode, Spread, SerializedLexicalNode, $isTextNode, KEY_BACKSPACE_COMMAND, COMMAND_PRIORITY_EDITOR, KEY_DELETE_COMMAND, TextNode, COMMAND_PRIORITY_HIGH } from "lexical";
import "./Editor.css";
import HtmlGeneratorPlugin from "./plugins/HtmlGeneratorPlugin";
import NodesGeneratorPlugin from "./plugins/NodesGeneratorPlugin";
import { TablePlugin } from "@lexical/react/LexicalTablePlugin";
import React, { useMemo, useRef, useState, useCallback, useEffect } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { LexicalTypeaheadMenuPlugin, MenuOption } from "@lexical/react/LexicalTypeaheadMenuPlugin";
import { List, ListItemButton, ListItemText, Paper, Popper } from "@mui/material";
import { TableNode, TableCellNode, TableRowNode, $isTableNode, $isTableCellNode, $getTableCellNodeFromLexicalNode, $getTableNodeFromLexicalNodeOrThrow } from "@lexical/table"
import ReactDOM from "react-dom";
import "../Table Insert Dropdown/TableStyles.css";
import Chip from "@mui/material/Chip"
import TextEditorToolbar from "./plugins/ToolbarPlugin";




function Placeholder() {
    return <div className="editor-placeholder">Enter here...</div>;
}

const Editor: React.FC<any> = (({
    onChange,
    initialContent,
    configType,
    mentionItems = [],
    getStageWiseConfigData = {},
    path = true,
    stage_id,
    isToolbar = true
}) => {
    function prepopulatedRichText(editor: any) {
        if (!initialContent) return;
        const root = $getRoot();
        if (root.getFirstChild() === null) {
            const parser = new DOMParser();
            const isValidJSON = (str: string) => {
                try {
                    JSON.parse(str);
                    return true;
                } catch (e) {
                    return false;
                }
            };
            const dom = parser.parseFromString(
                isValidJSON(initialContent) ? JSON.parse(initialContent) : initialContent,
                "text/html"
            );
            const nodes = $generateNodesFromDOM(editor, dom);
            $insertNodes(nodes);
            if (configType === "DYNAMIC" || configType === "PREVIOUS_STAGE") {
                const walk = (node: any) => {
                    if ($isTextNode(node)) {
                        const text = node.getTextContent();
                        const parts = text.split(/(\{[^}]+\})/g);
                        if (parts.length > 1) {
                            const nodesToInsert = parts
                                .map((p) => {
                                    const m = /^\{([^}]+)\}$/.exec(p);
                                    if (m) {
                                        return $createMentionNode({ key: m[1], label: m[1] });
                                    }
                                    return p ? $createTextNode(p) : null;
                                })
                                .filter(Boolean) as any[];

                            if (nodesToInsert.length) {
                                const first = nodesToInsert[0];
                                node.replace(first);
                                let last = first;
                                for (let i = 1; i < nodesToInsert.length; i++) {
                                    last.insertAfter(nodesToInsert[i]);
                                    last = nodesToInsert[i];
                                }
                            }
                        }
                    } else if (node && node.getChildren) {
                        node.getChildren().forEach(walk);
                    }
                };
                walk(root)
            }
        }
    }

    const editorConfig = {
        theme: EditorTheme,
        onError: (error: any) => {
            throw error;
        },
        namespace: "editor-tokenVariable",
        editable: path,
        editorState: (editor: any) => prepopulatedRichText(editor),
        nodes: [
            HeadingNode,
            ListNode,
            ListItemNode,
            QuoteNode,
            CodeNode,
            CodeHighlightNode,
            TableNode,
            TableCellNode,
            TableRowNode,
            AutoLinkNode,
            LinkNode,
            InlineMentionNode,
        ],
    };
    const lockFontStyle = useMemo(() => configType === "DYNAMIC" || configType === "PREVIOUS_STAGE",
        [configType]
    );

    return (
        <LexicalComposer initialConfig={editorConfig} >
            <div className="editor-container">
                <div className="editor-toolbar">
                    <TextEditorToolbar disableFontStyle={lockFontStyle} />
                </div>
                <div className="editor-inner">
                    <RichTextPlugin
                        contentEditable={<ContentEditable className="editor-input" />}
                        placeholder={<Placeholder />}
                        ErrorBoundary={LexicalErrorBoundary}
                    />
                    <HtmlGeneratorPlugin onChange={onChange} />
                    {/* <NodesGeneratorPlugin initialJSON={initialContent} /> */}
                    <HistoryPlugin />
                    <AutoFocusPlugin />
                    <ListPlugin />
                    <LinkPlugin />
                    <TablePlugin />
                    <StyleGuardPlugin disableFontStyle={lockFontStyle} />
                    <MarkdownShortcutPlugin transformers={TRANSFORMERS} />

                    {configType === "DYNAMIC" && (
                        <MentionsPlugin items={mentionItems} />
                    )}

                    {configType === "PREVIOUS_STAGE" && (
                        <PreviousStageMentionsPlugin
                            data={getStageWiseConfigData}
                        />
                    )}
                </div>
            </div>
        </LexicalComposer>
    );
});

export default Editor;


function StyleGuardPlugin({ disableFontStyle }: { disableFontStyle: boolean }) {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        if (!disableFontStyle) return;
        return editor.registerNodeTransform(TextNode, (node: TextNode) => {
            const style = node.getStyle();
            if (!style) return;
            const cleaned = style
                .replace(/(^|;)\s*font-size\s*:[^;]*;?/gi, "$1")
                .replace(/(^|;)\s*font-family\s*:[^;]*;?/gi, "$1")
                .replace(/;;+/g, ";")
                .replace(/^;|;$/g, "")
                .trim();
            if (cleaned !== style) {
                node.setStyle(cleaned);
            }
        });
    }, [editor, disableFontStyle]);

    return null;
}

type MentionData = { key: string; label: string, stage_id?: any };

type SerializedMentionNode = Spread<
    { type: "mention"; version: 1; mention: MentionData },
    SerializedLexicalNode
>;

class InlineMentionNode extends DecoratorNode<JSX.Element> {
    __mention: MentionData;

    static getType(): string {
        return "mention";
    }

    static clone(node: InlineMentionNode): InlineMentionNode {
        return new InlineMentionNode(node.__mention, node.__key);
    }

    constructor(mention: MentionData, key?: string) {
        super(key);
        this.__mention = mention;
    }

    createDOM(): HTMLElement {
        return document.createElement("span");
    }

    updateDOM(): false {
        return false;
    }

    private getBackendLabel(): string {
        let displayLabel = this.__mention.key;

        displayLabel = displayLabel.replace(/&gt;/g, ">");

        if (displayLabel.includes(">")) {
            const parts = displayLabel.split(">");
            if (parts.length === 2) {
                const stageId = (this.__mention as any).stage_id || "unknown_stage";
                const afterPart = parts[1].trim();
                displayLabel = `${afterPart}.${stageId}`;
            }
        }

        return displayLabel;
    }

    decorate(): JSX.Element {
        console.log("[InlineMentionNode] decorate called with:", this.__mention);
        return (
            <Chip
                label={this.__mention.label.replace(/&gt;/g, ">")}
                size="small"
                sx={{
                    fontSize: "12px",
                    pointerEvents: "none",
                    userSelect: "none",
                }}
            />
        );
    }

    isInline(): boolean {
        return true;
    }

    isIsolated(): boolean {
        return true;
    }

    static importJSON(serializedNode: SerializedMentionNode): InlineMentionNode {
        return new InlineMentionNode(serializedNode.mention);
    }

    exportJSON(): SerializedMentionNode {
        return {
            type: "mention",
            version: 1,
            mention: this.__mention,
        };
    }

    static importDOM(): any {
        return {
            span: (domNode: HTMLElement) => {
                if (domNode.hasAttribute("data-mention-key")) {
                    return {
                        conversion: () =>
                            new InlineMentionNode({
                                key: domNode.getAttribute("data-mention-key") || "",
                                label:
                                    domNode.textContent?.replace(/[{}]/g, "").trim() || "",
                            }),
                        priority: 1,
                    };
                }
                return null;
            },
        };
    }

    exportDOM(): { element: HTMLElement } {
        const backendLabel = this.getBackendLabel();
        const el = document.createElement("span");
        el.setAttribute("data-mention-key", this.__mention.key);
        el.setAttribute("class", "mention-chip");
        el.textContent = `{${backendLabel}}`;
        return { element: el };
    }
}


function $createMentionNode(mention: MentionData): InlineMentionNode {
    return new InlineMentionNode(mention);
}


type MentionItem = { key: string; value: string };
type MentionOption = MentionItem & {
    key: string;
    __raw: MentionItem;
    setRefElement: (element: null | HTMLElement) => void;
};

export function MentionsPlugin({ items }: { items: MentionItem[] }) {
  const [editor] = useLexicalComposerContext();
  const [query, setQuery] = useState<string | null>(null);
  const lastReplaceLenRef = useRef<number>(0);

  // CHANGE: only filter by query, not by selectedOptionKeys
  const filtered = useMemo(() => {
    if (!query) return items;

    const q = query.toLowerCase();
    return items.filter(
      (i) => i.key.toLowerCase().includes(q) || i.value.toLowerCase().includes(q)
    );
  }, [items, query]);

  const options: MentionOption[] = useMemo(
    () =>
      filtered.map((it) => ({
        ...it,
        __raw: it,
        key: it.key,
        setRefElement() {},
      })),
    [filtered]
  );

  const onSelect = useCallback(
    (opt: MentionOption) => {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          const anchorNode = selection.anchor.getNode();

          if ($isTextNode(anchorNode)) {
            const offset = selection.anchor.offset;
            const text = anchorNode.getTextContent();
            const match = /@[\w-]*$/.exec(text.slice(0, offset));

            if (match) {
              const startOffset = offset - match[0].length;
              selection.setTextNodeRange(anchorNode, startOffset, anchorNode, offset);
              selection.insertText("");
              const mentionNode = $createMentionNode({
                key: opt.__raw.key,
                label: opt.__raw.value,
              });
              $insertNodes([mentionNode]);
            }
          }
        }
      });
    },
    [editor]
  );

  return (
    <LexicalTypeaheadMenuPlugin<MentionOption>
      triggerFn={(text) => {
        const match = /(^|\s)@([\w-]*)$/.exec(text);
        if (!match) return null;
        const replaceableString = match[0];
        const matchingString = match[2];
        const leadOffset = text.length - replaceableString.length;
        lastReplaceLenRef.current = replaceableString.length;
        return { leadOffset, matchingString, replaceableString };
      }}
      onQueryChange={setQuery}
      options={options}
      onSelectOption={onSelect}
      menuRenderFn={(anchorElem, { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }) => {
        if (!anchorElem || options.length === 0) return null;

        return ReactDOM.createPortal(
          <Popper
            open
            anchorEl={anchorElem.current}
            key={`mention-popper-${query ?? ""}-${options.length}`}
            placement="bottom-start"
            disablePortal={false}
            modifiers={[{ name: "offset", options: { offset: [0, 6] } }]}
          >
            <Paper elevation={3} sx={{ maxHeight: 240, overflowY: "auto", minWidth: 220 }}>
              <List dense role="listbox" sx={{ p: 0 }}>
                {options.map((opt, i) => (
                  <ListItemButton
                    key={opt.key}
                    role="option"
                    selected={i === selectedIndex}
                    onMouseEnter={() => setHighlightedIndex(i)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      selectOptionAndCleanUp(opt);
                    }}
                    sx={{ py: 0.5, px: 1.5 }}
                  >
                    <ListItemText
                      primary={opt.value}
                      primaryTypographyProps={{ fontSize: 13 }}
                      secondaryTypographyProps={{ fontSize: 11 }}
                      sx={{ "& .MuiListItemText-secondary": { opacity: 0.7 } }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Paper>
          </Popper>,
          document.body
        );
      }}
    />
  );
}


// NEW COMPONENT for PREVIOUS_STAGE
// It supports "@stage" then ".config" without space. Final inserted token: {stageName.config_identifier}

type StageWiseApi = {
    data?: {
        results?: Array<{
            id: any;
            name: string;
            stage_configs: Array<{
                name: string;
                identifier: string;
            }>;
        }>;
    };
};

type PSOptionRaw = {
    stage: string;
    stageId: any;
    config?: { name: string; identifier: string };
};

type PSOption = PSOptionRaw & MenuOption & { __raw: PSOptionRaw };

function PreviousStageMentionsPlugin({ data }: { data: StageWiseApi }) {
    const [editor] = useLexicalComposerContext();
    const [query, setQuery] = useState<string | null>(null);
    const [selectedOptionKeys, setSelectedOptionKeys] = useState<Set<string>>(new Set());
    const lastReplaceLenRef = useRef<number>(0);

    const stages = useMemo(() => {
        const res = data?.data?.results || [];
        return res.map(r => ({
            id: r.id,               // <-- keep stage_id
            name: r.name,
            configs: r.stage_configs || []
        }));
    }, [data]);

    const options = useMemo<PSOption[]>(() => {
        const allOptions: PSOption[] = [];

        for (const stage of stages) {
            const configs = stage.configs ?? [];

            for (const cfg of configs) {
                const fullLabel = `${stage.name}.${cfg.identifier}`;

                if (!selectedOptionKeys.has(fullLabel)) {
                    allOptions.push({
                        stage: stage.name,
                        stageId: stage.id,
                        config: { name: cfg.name, identifier: cfg.identifier },
                        key: fullLabel,
                        setRefElement() { },
                        __raw: {
                            stage: stage.name, config: { name: cfg.name, identifier: cfg.identifier },
                            stageId: stage.id
                        },
                    });
                }
            }
        }

        if (!query) return allOptions;

        return allOptions.filter(opt => {
            const label = `${opt.stage} > ${opt.config?.identifier}`;
            return label.toLowerCase().includes(query.toLowerCase());
        });
    }, [query, stages, selectedOptionKeys]);

    const onSelect = useCallback(
        (opt: PSOption) => {
            editor.update(() => {
                const selection = $getSelection();
                if ($isRangeSelection(selection)) {
                    const anchorNode = selection.anchor.getNode();

                    if ($isTextNode(anchorNode)) {
                        const offset = selection.anchor.offset;
                        const text = anchorNode.getTextContent();
                        const match = /@[\w.-]*$/.exec(text.slice(0, offset));

                        if (match) {
                            const startOffset = offset - match[0].length;
                            selection.setTextNodeRange(anchorNode, startOffset, anchorNode, offset);
                            selection.insertText("");
                        }
                    }
                }
                const fullLabel = `${opt.stage} > ${opt.config?.identifier}`;
                const chipNode = $createMentionNode({
                    key: fullLabel,
                    label: fullLabel,
                    stage_id: opt.stageId,
                });
                $insertNodes([chipNode]);
            });
        },
        [editor]
    );

    return (
        <LexicalTypeaheadMenuPlugin<PSOption>
            triggerFn={(text) => {
                const m = /(^|\s)@([A-Za-z0-9._-]*)$/.exec(text);
                if (!m) return null;
                const replaceableString = m[0];
                const matchingString = m[2];
                const leadOffset = text.length - replaceableString.length;
                lastReplaceLenRef.current = replaceableString.length;
                setQuery(matchingString);
                return { leadOffset, matchingString, replaceableString };
            }}
            onQueryChange={setQuery}
            options={options}
            menuRenderFn={(anchorElem, { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }) => {
                if (!anchorElem || options.length === 0) return null;
                return ReactDOM.createPortal(
                    <Popper
                        open
                        anchorEl={anchorElem.current}
                        placement="bottom-start"
                        disablePortal={false}
                        modifiers={[{ name: "offset", options: { offset: [0, 6] } }]}
                    >
                        <Paper elevation={3} sx={{ maxHeight: 260, overflowY: "auto", minWidth: 260 }}>
                            <List dense role="listbox" sx={{ p: 0 }}>
                                {options.map((opt, i) => {
                                    const primary = opt.config
                                        ? `${opt.stage} > ${opt.config.identifier}`
                                        : opt.stage;
                                    // const secondary = opt.config
                                    //     ? `{${opt.stage}.${opt.config.identifier}}`
                                    //     : `{${opt.stage}}`;

                                    return (
                                        <ListItemButton
                                            key={opt.key}
                                            role="option"
                                            selected={i === selectedIndex}
                                            onMouseEnter={() => setHighlightedIndex(i)}
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                selectOptionAndCleanUp(opt);
                                            }}
                                            sx={{ py: 0.5, px: 1.5 }}
                                        >
                                            <ListItemText
                                                primary={primary}
                                                // secondary={secondary}
                                                primaryTypographyProps={{ fontSize: 13 }}
                                                secondaryTypographyProps={{ fontSize: 11 }}
                                                sx={{ "& .MuiListItemText-secondary": { opacity: 0.7 } }}
                                            />
                                        </ListItemButton>
                                    );
                                })}
                            </List>
                        </Paper>
                    </Popper>,
                    document.body
                );
            }}
            onSelectOption={onSelect}
        />
    );
}